import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { apiRequest } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { getCategoryColor } from "@/lib/utils";
import { Sparkles, Bell, Plus } from "lucide-react";

interface JournalingAssistantProps {
  athleteId?: string;
}

const JournalingAssistant = ({ athleteId }: JournalingAssistantProps) => {
  const queryClient = useQueryClient();
  const [customPromptOpen, setCustomPromptOpen] = useState(false);
  const [newPrompt, setNewPrompt] = useState({ title: "", prompt: "", category: "" });
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [entryContent, setEntryContent] = useState("");
  const [enableAI, setEnableAI] = useState(true);
  
  // Get journal prompts
  const { data: prompts, isLoading } = useQuery({
    queryKey: ["/api/journal-prompts"],
  });
  
  // Generate AI prompts mutation
  const generatePromptsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/athletes/${athleteId}/journal-prompts/generate`, {
        recentActivities: []
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal-prompts"] });
      toast({
        title: "New prompts generated",
        description: "AI has created personalized journal prompts for you"
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to generate prompts. Please try again.",
        variant: "destructive"
      });
    }
  });
  
  // Create journal entry mutation
  const createEntryMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest('POST', `/api/athletes/${athleteId}/journal`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "journal"] });
      toast({
        title: "Journal entry created",
        description: "Your new journal entry has been saved"
      });
      setEntryFormOpen(false);
      setSelectedPrompt(null);
      setEntryContent("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create journal entry",
        variant: "destructive"
      });
    }
  });

  const handlePromptClick = (prompt: any) => {
    setSelectedPrompt(prompt);
    setEntryContent("");
    setEntryFormOpen(true);
  };

  const handleCreateEntry = () => {
    if (!entryContent.trim()) {
      toast({
        title: "Error",
        description: "Please write content for your journal entry",
        variant: "destructive"
      });
      return;
    }

    createEntryMutation.mutate({
      title: selectedPrompt?.title || "Journal Entry",
      content: entryContent,
      date: new Date().toISOString().split("T")[0],
      tags: selectedPrompt?.category ? [selectedPrompt.category] : [],
      enhanceWithAI: enableAI
    });
  };

  const createCustomPrompt = () => {
    if (!newPrompt.title || !newPrompt.prompt) {
      toast({
        title: "Error",
        description: "Title and prompt content are required",
        variant: "destructive"
      });
      return;
    }

    setSelectedPrompt({
      title: newPrompt.title,
      prompt: newPrompt.prompt,
      category: newPrompt.category
    });
    
    setCustomPromptOpen(false);
    setEntryFormOpen(true);
  };

  // Use recent prompts, or fallback to default prompts if loading
  const recentPrompts = isLoading || !prompts ? 
    [
      { title: "Reflect on your performance", prompt: "How do you feel about your recent game or practice?", category: "Game Reflection" },
      { title: "Training milestone", prompt: "Describe a recent breakthrough in your training", category: "Training" },
      { title: "Leadership moment", prompt: "Share a moment when you demonstrated leadership", category: "Leadership" }
    ] 
    : prompts.slice(0, 3);

  return (
    <>
      <Card className="shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 border-b border-[#E0A458]/30 p-4">
          <div className="flex items-center">
            <Sparkles className="text-primary mr-2 h-5 w-5" />
            <h2 className="font-montserrat font-bold text-lg text-white">AI Journaling Assistant</h2>
          </div>
        </div>
        
        <CardContent className="p-4">
          <p className="text-slate-200 text-sm mb-3">
            Need inspiration for your next journal entry? Try one of these personalized prompts:
          </p>
          
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-full h-20 rounded-lg border border-[#A67C52] animate-pulse bg-[#0F1B2D]"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {recentPrompts.map((prompt, index) => (
                <button 
                  key={index}
                  className="w-full text-left p-3 rounded-lg border border-[#A67C52] hover:border-[#E0A458] hover:bg-[#0F1B2D] transition"
                  onClick={() => handlePromptClick(prompt)}
                >
                  <p className="font-medium text-white">{prompt.title}</p>
                  <p className="text-slate-200 text-sm">{prompt.prompt}</p>
                </button>
              ))}
            </div>
          )}
          
          <div className="flex flex-col gap-2 mt-4">
            <Button
              variant="ghost"
              className="w-full text-slate-300 hover:text-white hover:bg-[#1B263B] text-sm font-medium"
              onClick={() => setCustomPromptOpen(true)}
            >
              Create custom prompt
            </Button>

            {!isLoading && (
              <Button
                className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                onClick={() => generatePromptsMutation.mutate()}
                disabled={generatePromptsMutation.isPending}
              >
                {generatePromptsMutation.isPending ? "Generating..." : "Generate new AI prompts"}
              </Button>
            )}
          </div>
        </CardContent>
        
        <div className="border-t border-[#A67C52] px-4 py-3 bg-[#0F1B2D]">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-slate-200">
              <Bell className="text-slate-400 mr-2 h-4 w-4" />
              <span className="text-sm">Weekly prompt reminders</span>
            </div>
            <Switch checked={true} />
          </div>
        </div>
      </Card>
      
      {/* Custom Prompt Dialog */}
      <Dialog open={customPromptOpen} onOpenChange={setCustomPromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a Custom Prompt</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="prompt-title">Prompt Title</Label>
              <Input 
                id="prompt-title" 
                placeholder="e.g., Game Day Reflection" 
                value={newPrompt.title}
                onChange={(e) => setNewPrompt({...newPrompt, title: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="prompt-content">Prompt Question</Label>
              <Textarea 
                id="prompt-content" 
                placeholder="e.g., How did you handle pressure during today's game?" 
                rows={3}
                value={newPrompt.prompt}
                onChange={(e) => setNewPrompt({...newPrompt, prompt: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="prompt-category">Category (optional)</Label>
              <Input 
                id="prompt-category" 
                placeholder="e.g., Game Reflection, Leadership, Training" 
                value={newPrompt.category}
                onChange={(e) => setNewPrompt({...newPrompt, category: e.target.value})}
              />
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setCustomPromptOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createCustomPrompt}>
              Create Prompt
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Journal Entry Dialog */}
      <Dialog open={entryFormOpen} onOpenChange={setEntryFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPrompt?.title || "New Journal Entry"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedPrompt && (
              <div className={`p-3 rounded-md ${getCategoryColor(selectedPrompt.category).replace('text-', 'bg-').replace('/10', '/20')}`}>
                <p className="text-sm font-medium">{selectedPrompt.prompt}</p>
              </div>
            )}
            
            <Textarea 
              placeholder="Write your journal entry here..." 
              rows={10}
              value={entryContent}
              onChange={(e) => setEntryContent(e.target.value)}
            />
            
            <div className="flex items-center">
              <Switch 
                id="ai-enhance" 
                checked={enableAI}
                onCheckedChange={setEnableAI}
              />
              <Label htmlFor="ai-enhance" className="ml-2">
                Enhance with AI
              </Label>
              <div className="text-xs text-slate-300 ml-2">
                (Improve clarity and generate tags)
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEntryFormOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEntry}
              disabled={createEntryMutation.isPending}
              className="bg-primary"
            >
              {createEntryMutation.isPending ? "Saving..." : "Save Journal Entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default JournalingAssistant;
